import React, { Fragment } from 'react';
import { styled } from '@mui/material/styles';
import SwapHorizontalCircleIcon from '@mui/icons-material/SwapHorizontalCircle';
import Tooltip from '@mui/material/Tooltip';
import { momentFormat } from '#helper';

const GridContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  gap: theme.spacing(0, 1),
  alignItems: 'center'
}));

const GridItem = styled('div')({
  textAlign: 'left'
});

/**
 * A React component that displays icon and tooltip with a list of date ranges
 * when there is more than one date range or the date range has end date
 * or start date is later than or equal to query start date.
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.account - The account object used for formatting date ranges.
 * @param {Array<{ start: string, end: string }>} props.dateRanges
 * - An array of date range objects, where each has a `start` and `end` property.
 * @param {Object} [props.iconProps]
 * - Additional properties passed to the `SwapHorizontalCircleIcon`.
 * @returns {JSX.Element|null} The rendered component or null.
 */
export default function DateRangesIcon(props) {
  const { account, dateRanges, iconProps, queryRangeStart } = props;

  const shallDisplayTooltip =
    dateRanges?.length > 1 ||
    dateRanges?.at(-1)?.end ||
    dateRanges?.at(-1)?.start >= queryRangeStart;

  if (!shallDisplayTooltip) {
    return null;
  }

  return (
    <Tooltip
      title={
        <GridContainer>
          {dateRanges.map(({ start, end }) => {
            const labels = [];
            const dates = [];

            if (start) {
              labels.push('Added');
              dates.push(momentFormat(start, account, 'dateTimeShort'));
            }

            if (end) {
              labels.push(start ? 'removed' : 'Removed');
              dates.push(momentFormat(end, account, 'dateTimeShort'));
            }

            const labelText = labels.join('/');
            const dateText = dates.join(' - ');

            return (
              <Fragment key={labelText + dateText}>
                <GridItem>{labelText}</GridItem>
                <GridItem>{dateText}</GridItem>
              </Fragment>
            );
          })}
        </GridContainer>
        }
    >
      <SwapHorizontalCircleIcon {...iconProps} />
    </Tooltip>
  );
}
